// BillingProgressRing.jsx
"use client";

import React from "react";
import styles from "./billingProgress.module.css";

const BillingProgressRing = ({
  size = 42,
  segments = [], // array of { key, label, completed, weight, subSegments? }
  label,
  isLight = false,
  indicator = null, // original indicator data for calculating main segment count
}) => {
  if (!Array.isArray(segments) || segments.length === 0) return null;

  const radius = 14;
  const stroke = 3;
  const circumference = 2 * Math.PI * radius;
  const gap = 4; // px gap between segments (visual spacing)
  const colors = {
    complete: isLight ? "#2ECC71" : "#1FBF75",
    incomplete: isLight ? "#F6C177" : "#F4A261",
    track: "#E5E7EB",
    text: isLight ? "#FFFFFF" : "#1A1A1A",
    label: isLight ? "#FFFFFF" : "#6B7280",
    border: "#FFFFFF", // White border for all segments
  };

  // total weight for main segments
  const totalWeight = segments.reduce((s, seg) => s + (seg.weight || 1), 0);
  
 // No gaps - use full circumference
  let offset = 0;
  const circles = [];
  // Calculate completion count based on main segments from API
  let completedMain = 0;
  let totalMain = 0;
  
  if (indicator?.segments) {
    const s = indicator.segments;
    if (label === "RPM") {
      totalMain = 3;
      if (s.device_readings?.completed) completedMain++;
      if (s.two_way_communication?.completed) completedMain++;
      // For time_based, check if any sub_segment is completed (ignore time_based.completed)
      const timeSubs = s.time_based?.sub_segments || {};
      const hasAnyTimeCompleted = Object.values(timeSubs).some(val => 
        typeof val === "boolean" ? val : Boolean(val?.completed ?? val)
      );
      if (hasAnyTimeCompleted) completedMain++;
    } else if (label === "CCM") {
      // CCM main segments: Two-Way Communication (derived from care_plan_documented), care_plan_documented, care_plan_finalized, time_based
      totalMain = 4; 
      if (s.care_plan_documented?.completed) completedMain++; 
      if (s.care_plan_documented?.completed) completedMain++; 
      if (s.care_plan_finalized?.completed) completedMain++;
      const timeSubs = s.time_based?.sub_segments || {};
      const hasAnyTimeCompleted = Object.values(timeSubs).some(val => 
        typeof val === "boolean" ? val : Boolean(val?.completed ?? val)
      );
      if (hasAnyTimeCompleted) completedMain++;
    } else {
      completedMain = segments.filter(seg => seg.completed).length;
      totalMain = segments.length;
    }
  } else {
    completedMain = segments.filter(seg => seg.completed).length;
    totalMain = segments.length;
  }


  segments.forEach((seg, i) => {
    // Calculate arc length based on weight proportion (no gaps)
    const arcLen = (seg.weight || 1) / totalWeight * circumference;
    const strokeColor = seg.completed ? colors.complete : colors.incomplete;

    // Add colored segment on top
    circles.push(
      <circle
        key={`main-${seg.key}-${i}`}
        cx="16"
        cy="16"
        r={radius}
        fill="none"
        stroke={strokeColor}
        strokeWidth={stroke}
        strokeDasharray={`${arcLen} ${circumference}`}
        strokeDashoffset={-offset}
        strokeLinecap="round"
      />
    );

    // draw sub-segments (inner thin ticks)
    if (Array.isArray(seg.subSegments) && seg.subSegments.length > 0) {
      const subTotal = seg.subSegments.reduce((s, ss) => s + (ss.weight || 1), 0);
      let subOffset = offset;
      seg.subSegments.forEach((ss, si) => {
        const subLen = (ss.weight || 1) / subTotal * arcLen;
        circles.push(
          <circle
            key={`sub-${seg.key}-${si}`}
            cx="16"
            cy="16"
            r={radius - 1.6} // slightly inset
            fill="none"
            stroke={ss.completed ? colors.complete : colors.incomplete}
            strokeWidth={1.2}
            strokeDasharray={`${subLen} ${circumference}`}
            strokeDashoffset={-subOffset}
            strokeLinecap="round"
          />
        );
        subOffset += subLen;
      });
    }

    offset += arcLen;
  });

 return (
  <div className={styles.ringWrapper} style={{ width: size, height: size }}>
    <svg width={size} height={size} viewBox="0 0 32 32">
      <g transform="rotate(-90 16 16)">{circles}</g>
    </svg>

    {/* CENTER TEXT (COUNT + LABEL) */}
    <div className={styles.centerText} style={{ color: colors.text }}>
      {/* <div style={{ fontSize: "10px", fontWeight: 600, lineHeight: 1 }}>
        {completedMain}/{totalMain}
      </div> */}

      {label && (
        <div
          style={{
            fontSize: "10px",
            marginTop: 2,
            opacity: 0.85,
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      )}
    </div>
  </div>
);

};

export default BillingProgressRing;
